// frontend/src/pages/JefeDashboard/JefeInvitaciones.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInbox, FaCheck, FaTimes, FaCalendar, FaFolder, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';

export default function JefeInvitaciones() {
  const [invitaciones, setInvitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [toast, setToast] = useState({ visible: false, type: 'success', title: '', message: '' });

  useEffect(() => {
    if (!toast.visible) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.visible]);

  const toUIEstado = (estado) => {
    switch (estado) {
      case 'PENDIENTE': return 'Pendiente';
      case 'ACTIVO': return 'En Progreso';
      case 'RECHAZADO': return 'Cancelado';
      default: return estado || 'Pendiente';
    }
  };

  useEffect(() => {
    const cargarInvitaciones = async () => {
      try {
        setLoading(true);
        const data = await fetchAuth('/api/jefe/proyectos/');
        const proyectosFormateados = (data.proyectos || []).map(proyecto => ({
          id_proyecto: proyecto.id_proyecto,
          nombre_proyecto: proyecto.nombre_proyecto,
          descripcion: proyecto.descripcion,
          fecha_asignacion: proyecto.fecha_inicio,
          estado: toUIEstado(proyecto.estado)
        }));
        
        // Filtrar solo pendientes
        const pendientes = proyectosFormateados.filter(inv => inv.estado === 'Pendiente');
        setInvitaciones(pendientes);
      } catch (err) {
        console.error('Error al cargar invitaciones:', err);
        setError(err.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    
    cargarInvitaciones();
  }, []);

  const handleAceptarProyecto = async (idProyecto) => {
    try {
      setProcessingId(idProyecto);
      await fetchAuth(`/api/jefe/proyectos/${idProyecto}/aceptar/`, {
        method: 'POST'
      });
      
      // Remover de la lista
      setInvitaciones(prev => prev.filter(inv => inv.id_proyecto !== idProyecto));
      setToast({ visible: true, type: 'success', title: 'Invitación aceptada', message: 'Has aceptado participar en el proyecto.' });
    } catch (error) {
      console.error('Error al aceptar proyecto:', error);
      setToast({ visible: true, type: 'error', title: 'No se pudo aceptar', message: error.message || 'Intenta nuevamente.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleRechazarProyecto = async (idProyecto) => {
    if (!window.confirm('¿Estás seguro de que deseas rechazar este proyecto?')) {
      return;
    }
    
    try {
      setProcessingId(idProyecto);
      await fetchAuth(`/api/jefe/proyectos/${idProyecto}/rechazar/`, {
        method: 'POST'
      });
      
      // Remover de la lista
      setInvitaciones(prev => prev.filter(inv => inv.id_proyecto !== idProyecto));
      setToast({ visible: true, type: 'success', title: 'Invitación rechazada', message: 'Has rechazado la invitación del proyecto.' });
    } catch (error) {
      console.error('Error al rechazar proyecto:', error);
      setToast({ visible: true, type: 'error', title: 'No se pudo rechazar', message: error.message || 'Intenta nuevamente.' });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <FaSpinner className="animate-spin text-5xl text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">Cargando invitaciones...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-800 text-xl font-bold mb-2">Error al cargar</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-6">
      <AnimatePresence>
        {toast.visible && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className={`flex items-start gap-3 rounded-xl border shadow-lg px-4 py-3 ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'} text-xl`}>
                  {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{toast.title}</p>
                  <p className="text-sm text-slate-600">{toast.message}</p>
                </div>
                <button
                  onClick={() => setToast(prev => ({ ...prev, visible: false }))}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Cerrar notificación"
                >
                  <FaTimes />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-4 mb-3">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-2xl shadow-lg">
            <FaInbox className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
              Invitaciones de Proyecto
            </h1>
            <p className="text-slate-600 mt-1">
              Acepta o rechaza tu participación antes de que el proyecto se active
            </p>
          </div>
        </div>
        
        {/* Counter badge */}
        {invitaciones.length > 0 && (
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold text-sm mt-4">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            {invitaciones.length} {invitaciones.length === 1 ? 'invitación pendiente' : 'invitaciones pendientes'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {invitaciones.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-16 text-center border border-slate-200">
            <div className="bg-slate-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaInbox className="text-slate-400 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">
              No hay invitaciones pendientes
            </h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Cuando recibas invitaciones para nuevos proyectos, aparecerán aquí para que puedas revisarlas.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {invitaciones.map((inv) => (
              <div
                key={inv.id_proyecto}
                className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 overflow-hidden group"
              >
                {/* Card Header con gradiente */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <span className="text-white text-xs font-semibold flex items-center gap-1.5">
                          <FaFolder className="text-white/80" />
                          Proyecto Nuevo
                        </span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 line-clamp-2">
                      {inv.nombre_proyecto}
                    </h3>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                    {inv.descripcion || 'Sin descripción disponible'}
                  </p>

                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-6 bg-slate-50 rounded-xl p-3">
                    <FaCalendar className="text-slate-400" />
                    <span>
                      Asignado el <strong className="text-slate-700">{formatDate(inv.fecha_asignacion)}</strong>
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleAceptarProyecto(inv.id_proyecto)}
                      disabled={processingId === inv.id_proyecto}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-green-500/30 hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === inv.id_proyecto ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <>
                          <FaCheck />
                          <span>Aceptar</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleRechazarProyecto(inv.id_proyecto)}
                      disabled={processingId === inv.id_proyecto}
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === inv.id_proyecto ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <>
                          <FaTimes />
                          <span>Rechazar</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '-'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }
